/**
 * Mantenimiento propuesto (Req. 22.1, 22.4 a 22.8, 22.10) por dos caminos:
 *
 * - Por fórmula (design.md, 8.6): Mifflin-St Jeor por el factor de actividad.
 * - Estimado con los días registrados (design.md, 8.7): el promedio de kcal
 *   menos el cambio de la tendencia de peso.
 *
 * Los dos solo proponen: nada cambia sin que la persona acepte o modifique
 * (Req. 22.5). Si falta un dato, se dice cuál en lugar de suponerlo
 * (Req. 22.7), y siempre se muestra de dónde sale el número, paso a paso
 * (Req. 22.6, 22.10).
 */

import type { MacrosDiarios, Maintenance, NivelActividad, Sexo } from '@/db/types';
import { formatearCorto, type FechaLocal } from './dates';
import { formatearNumero, redondearAMultiplo } from './numbers';
import { edadEn } from './perfil';
import { formatearKcal } from './nutricion';
import { formatearLongitud, formatearPeso, type UnidadLongitud, type UnidadPeso } from './units';

/** design.md, 8.6. Aproximados: se muestran y se pueden revisar. */
export const FACTOR_ACTIVIDAD: Readonly<Record<NivelActividad, number>> = {
  sedentario: 1.2,
  ligero: 1.375,
  moderado: 1.55,
  alto: 1.725,
  muy_alto: 1.9,
};

/** Una propuesta rechazada no se vuelve a ofrecer hasta que cambie esto (Req. 22.5). */
export const DIFERENCIA_PARA_REOFRECER_KCAL = 50;

/** Lo que hace falta para la fórmula, en el orden en que se pide. */
export type DatoDeFormula = 'peso' | 'estatura' | 'nacimiento' | 'sexo' | 'actividad';

export const NOMBRE_DATO: Readonly<Record<DatoDeFormula, string>> = {
  peso: 'tu peso',
  estatura: 'tu estatura',
  nacimiento: 'tu fecha de nacimiento',
  sexo: 'si eres mujer u hombre',
  actividad: 'tu nivel de actividad',
};

/** Dónde se captura cada dato que falta. */
export const DONDE_CAPTURAR: Readonly<Record<DatoDeFormula, string>> = {
  peso: '/cuerpo/pesaje/nuevo',
  estatura: '/cuerpo/estatura',
  nacimiento: '/mas/mantenimiento',
  sexo: '/mas/mantenimiento',
  actividad: '/mas/mantenimiento',
};

export interface DatosDeFormula {
  /** La tendencia de hoy o, sin tendencia, el último pesaje. */
  pesoKg: number | null;
  /** De dónde salió el peso, para decirlo. */
  pesoDe: 'tendencia' | 'ultimo' | null;
  estaturaCm: number | null;
  fechaNacimiento: FechaLocal | null;
  sexo: Sexo | null;
  nivelActividad: NivelActividad | null;
}

export interface PropuestaPorFormula {
  kcal: number;
  /** Antes de redondear a 10, para el paso a paso. */
  sinRedondear: number;
  tmb: number;
  factor: number;
  edad: number;
  nivelActividad: NivelActividad;
  pesoKg: number;
  pesoDe: 'tendencia' | 'ultimo';
  estaturaCm: number;
  sexo: Sexo;
  /** Cada renglón del cálculo, con sus números (Req. 22.10). */
  pasos: string[];
  /** Lo que la fórmula da por cierto (Req. 22.6). */
  supuestos: string[];
  /** Una línea para el historial. */
  detalle: string;
  /** Los macros que se proponen con esas kcal (0.5.1); null si no caben. */
  macros: MacrosPropuestos | null;
}

export type ResultadoFormula =
  | { tipo: 'listo'; propuesta: PropuestaPorFormula }
  | { tipo: 'faltan'; faltan: DatoDeFormula[] };

const ORDEN: readonly DatoDeFormula[] = ['peso', 'estatura', 'nacimiento', 'sexo', 'actividad'];

/** Lo que falta para la fórmula, en orden; vacío si no falta nada (Req. 22.7). */
export function faltantesDeFormula(datos: DatosDeFormula): DatoDeFormula[] {
  const falta: Record<DatoDeFormula, boolean> = {
    peso: datos.pesoKg === null || !(datos.pesoKg > 0),
    estatura: datos.estaturaCm === null || !(datos.estaturaCm > 0),
    nacimiento: datos.fechaNacimiento === null,
    sexo: datos.sexo === null,
    actividad: datos.nivelActividad === null,
  };
  return ORDEN.filter((d) => falta[d]);
}

const unDecimal = (v: number) => formatearNumero(v, { decimales: 1, fijos: false });
/** Un peso siempre con su decimal, como en el resto de la app: "70.0 kg". */
const unDecimalFijo = (v: number) => formatearNumero(v, { decimales: 1 });
const dosDecimales = (v: number) => formatearNumero(v, { decimales: 2, fijos: false });
const sinDecimales = (v: number) => formatearNumero(v, { decimales: 0 });

/**
 * La propuesta por fórmula a la fecha indicada, o la lista de lo que falta.
 * Las unidades solo cambian cómo se lee el paso a paso: el cálculo va en kg y
 * cm, como la fórmula.
 */
export function propuestaPorFormula(
  datos: DatosDeFormula,
  hoy: FechaLocal,
  unidades: { peso: UnidadPeso; longitud: UnidadLongitud } = { peso: 'kg', longitud: 'cm' },
): ResultadoFormula {
  const faltan = faltantesDeFormula(datos);
  if (faltan.length > 0) return { tipo: 'faltan', faltan };
  // faltantesDeFormula ya revisó que estos existan
  const pesoKg = datos.pesoKg as number;
  const estaturaCm = datos.estaturaCm as number;
  const sexo = datos.sexo as Sexo;
  const nivelActividad = datos.nivelActividad as NivelActividad;
  const pesoDe = datos.pesoDe ?? 'ultimo';
  const edad = edadEn(datos.fechaNacimiento as FechaLocal, hoy);
  const ajuste = sexo === 'hombre' ? 5 : -161;
  const tmb = 10 * pesoKg + 6.25 * estaturaCm - 5 * edad + ajuste;
  const factor = FACTOR_ACTIVIDAD[nivelActividad];
  const sinRedondear = tmb * factor;
  const kcal = redondearAMultiplo(sinRedondear, 10);

  const pesoTexto = formatearPeso(pesoKg, unidades.peso);
  const estaturaTexto = formatearLongitud(estaturaCm, unidades.longitud);
  const enKgYCm =
    unidades.peso === 'kg' && unidades.longitud === 'cm' ? '' : ` (${unDecimal(pesoKg)} kg y ${unDecimal(estaturaCm)} cm)`;
  const pasos = [
    `Tus datos: ${pesoTexto}, ${estaturaTexto}, ${edad} años, ${sexo === 'hombre' ? 'hombre' : 'mujer'}${enKgYCm}.`,
    `Metabolismo en reposo = 10 × ${unDecimal(pesoKg)} + 6.25 × ${unDecimal(estaturaCm)} − 5 × ${edad} ${
      sexo === 'hombre' ? '+ 5' : '− 161'
    } = ${unDecimal(tmb)} kcal.`,
    `Por tu actividad (${ETIQUETA_ACTIVIDAD[nivelActividad]}): ${unDecimal(tmb)} × ${dosDecimales(factor)} = ${unDecimal(sinRedondear)} kcal.`,
    `Redondeado a 10: ${formatearKcal(kcal)} al día.`,
  ];
  const macros = macrosDeMantenimiento(kcal, pesoKg);
  const supuestos = [
    'Fórmula de Mifflin-St Jeor, la más usada para estimar el metabolismo en reposo.',
    `El factor de actividad (${dosDecimales(factor)}) es aproximado: agrupa todo tu movimiento del día, no lo mide.`,
    pesoDe === 'tendencia'
      ? 'El peso es tu tendencia de 7 días, no el pesaje de un solo día.'
      : 'Todavía no hay tendencia de 7 días: se usó tu último pesaje.',
    'Es una estimación, no una medición: tu mantenimiento real se confirma viendo cómo responde tu peso.',
    ...(macros === null
      ? []
      : [
          `Proteína de ${formatearNumero(PROTEINA_POR_KG, { decimales: 1 })} g por kg de peso y grasa del ${
            macros.porcentajes.grasa
          } % de las calorías; los carbohidratos son el resto. Es un punto de partida para revisar, no una indicación profesional.`,
        ]),
  ];
  const detalle = `Fórmula de Mifflin-St Jeor con ${pesoTexto}, ${estaturaTexto}, ${edad} años y actividad ${ETIQUETA_ACTIVIDAD[
    nivelActividad
  ].toLocaleLowerCase('es-MX')}.${
    macros === null ? '' : ` Macros: ${macros.proteinaG} g de proteína, ${macros.carbohidratosG} g de carbohidratos y ${macros.grasaG} g de grasa.`
  }`;

  return {
    tipo: 'listo',
    propuesta: {
      kcal,
      sinRedondear,
      tmb,
      factor,
      edad,
      nivelActividad,
      pesoKg,
      pesoDe,
      estaturaCm,
      sexo,
      pasos,
      supuestos,
      detalle,
      macros,
    },
  };
}

/** Nombre corto de cada nivel, para el paso a paso. */
export const ETIQUETA_ACTIVIDAD: Readonly<Record<NivelActividad, string>> = {
  sedentario: 'Sedentario',
  ligero: 'Ligero',
  moderado: 'Moderado',
  alto: 'Alto',
  muy_alto: 'Muy alto',
};

// ---------------------------------------------------------------------------
// Macros del mantenimiento (0.5.1)
// ---------------------------------------------------------------------------

/**
 * Criterio documentado (design.md, 8.6b). Es un punto de partida para
 * revisar, no una indicación profesional: lo que diga una nutrióloga manda.
 *
 * - Proteína: 1.6 g por kg de peso. En el metaanálisis de Morton y
 *   colaboradores (2018) el beneficio para conservar músculo deja de subir
 *   alrededor de ese valor, así que es el extremo bajo de lo recomendado.
 * - Grasa: 25 % de las calorías, en medio del intervalo aceptado (20 a 35 %).
 * - Carbohidratos: lo que queda, que es lo que sostiene el entrenamiento.
 *
 * Todo en múltiplos de 5 g, porque así se pesa y se anota.
 */
export const PROTEINA_POR_KG = 1.6;
export const PORCENTAJE_GRASA = 0.25;
/** Si la grasa al 25 % no deja carbohidratos suficientes, se baja a este mínimo. */
export const PORCENTAJE_GRASA_MINIMO = 0.2;
/** Menos de esto en carbohidratos ya no es una propuesta razonable: no se propone. */
export const CARBOHIDRATOS_MINIMOS_G = 50;
/** Diferencia aceptada entre las kcal de los macros y las propuestas, por el redondeo a 5 g. */
export const TOLERANCIA_KCAL = 25;

export const KCAL_POR_G = { proteina: 4, carbohidratos: 4, grasa: 9 } as const;

export interface MacrosPropuestos extends MacrosDiarios {
  /** 4 × proteína + 4 × carbohidratos + 9 × grasa. */
  kcalDeLosMacros: number;
  /** kcalDeLosMacros − las kcal propuestas; el redondeo a 5 g la mueve unas pocas. */
  diferenciaKcal: number;
  /** Qué parte de las kcal aporta cada macro, ya redondeado. */
  porcentajes: { proteina: number; carbohidratos: number; grasa: number };
  /** Gramos de proteína por kg de peso, como salió después de redondear. */
  proteinaPorKg: number;
  pasos: string[];
}

const aCinco = (v: number) => redondearAMultiplo(v, 5);

/** Las kcal que suman unos macros. */
export function kcalDeMacros(m: MacrosDiarios): number {
  return KCAL_POR_G.proteina * m.proteinaG + KCAL_POR_G.carbohidratos * m.carbohidratosG + KCAL_POR_G.grasa * m.grasaG;
}

/** ¿Los macros cuadran con esas kcal, dentro de lo que mueve el redondeo? */
export function macrosCuadran(m: MacrosDiarios, kcal: number): boolean {
  return Math.abs(kcalDeMacros(m) - kcal) <= TOLERANCIA_KCAL;
}

/**
 * Los macros que se proponen con esas kcal y ese peso. null si no caben: con
 * muy pocas kcal para el peso, la proteína y la grasa no dejarían
 * carbohidratos suficientes y la app no propone algo que no se sostiene.
 */
export function macrosDeMantenimiento(kcal: number, pesoKg: number): MacrosPropuestos | null {
  if (!(kcal > 0) || !(pesoKg > 0)) return null;
  const proteinaG = aCinco(PROTEINA_POR_KG * pesoKg);
  for (const pctGrasa of [PORCENTAJE_GRASA, PORCENTAJE_GRASA_MINIMO]) {
    const grasaG = aCinco((kcal * pctGrasa) / KCAL_POR_G.grasa);
    const restan = kcal - KCAL_POR_G.proteina * proteinaG - KCAL_POR_G.grasa * grasaG;
    const carbohidratosG = aCinco(restan / KCAL_POR_G.carbohidratos);
    if (carbohidratosG < CARBOHIDRATOS_MINIMOS_G) continue;
    const macros = { proteinaG, carbohidratosG, grasaG };
    const total = kcalDeMacros(macros);
    const pct = (g: number, porG: number) => Math.round(((g * porG) / total) * 100);
    const bajamosLaGrasa = pctGrasa !== PORCENTAJE_GRASA;
    return {
      ...macros,
      kcalDeLosMacros: total,
      diferenciaKcal: total - kcal,
      porcentajes: {
        proteina: pct(proteinaG, KCAL_POR_G.proteina),
        carbohidratos: pct(carbohidratosG, KCAL_POR_G.carbohidratos),
        grasa: pct(grasaG, KCAL_POR_G.grasa),
      },
      proteinaPorKg: proteinaG / pesoKg,
      pasos: [
        `Proteína: ${formatearNumero(PROTEINA_POR_KG, { decimales: 1 })} g por kg × ${unDecimal(pesoKg)} kg = ${proteinaG} g (${sinDecimales(
          KCAL_POR_G.proteina * proteinaG,
        )} kcal).`,
        `Grasa: ${Math.round(pctGrasa * 100)} % de ${formatearKcal(kcal)} ÷ 9 = ${grasaG} g (${sinDecimales(KCAL_POR_G.grasa * grasaG)} kcal)${
          bajamosLaGrasa ? ', bajada del 25 % para que queden carbohidratos suficientes' : ''
        }.`,
        `Carbohidratos: lo que queda, ${sinDecimales(restan)} ÷ 4 = ${carbohidratosG} g (${sinDecimales(
          KCAL_POR_G.carbohidratos * carbohidratosG,
        )} kcal).`,
        `Suman ${sinDecimales(KCAL_POR_G.proteina * proteinaG)} + ${sinDecimales(
          KCAL_POR_G.carbohidratos * carbohidratosG,
        )} + ${sinDecimales(KCAL_POR_G.grasa * grasaG)} = ${formatearKcal(total)}, ${
          total === kcal
            ? 'justo tus kcal'
            : `${total > kcal ? '+' : '−'}${sinDecimales(Math.abs(total - kcal))} kcal por redondear a 5 g`
        }.`,
      ],
    };
  }
  return null;
}

export type RazonParaNoOfrecer = 'ya_es_el_vigente' | 'cerca_del_vigente' | 'rechazada_sin_cambio';

export type Ofrecimiento = { ofrecer: true } | { ofrecer: false; razon: RazonParaNoOfrecer; rechazadaKcal: number | null };

/**
 * ¿Se ofrece esta propuesta? (Req. 22.5)
 *
 * - No, si ya es igual al mantenimiento vigente: no hay nada que decidir.
 * - No, si es un estimado que queda a menos de 50 kcal del vigente: se
 *   recalcula cada día con lo que se registra y se movería unas pocas kcal
 *   cada vez; pedir una decisión por eso sería ruido (design.md, 8.7).
 * - No, si se rechazó una propuesta del mismo origen y la nueva no cambia
 *   50 kcal o más; entonces se dice cuál se rechazó, sin volver a preguntar.
 *
 * `registros` es el historial completo; se mira solo lo del mismo origen.
 */
export function debeOfrecerse(
  kcal: number,
  vigenteKcal: number | null,
  registros: readonly Pick<Maintenance, 'origen' | 'decision' | 'propuestaKcal' | 'creadoEn'>[],
  origen: 'formula' | 'estimado' = 'formula',
): Ofrecimiento {
  if (vigenteKcal !== null) {
    const diferencia = Math.abs(vigenteKcal - kcal);
    if (diferencia === 0) return { ofrecer: false, razon: 'ya_es_el_vigente', rechazadaKcal: null };
    if (origen === 'estimado' && diferencia < DIFERENCIA_PARA_REOFRECER_KCAL) {
      return { ofrecer: false, razon: 'cerca_del_vigente', rechazadaKcal: null };
    }
  }
  const rechazadas = registros
    .filter((r) => r.origen === origen && r.decision === 'rechazada' && r.propuestaKcal !== null)
    .sort((a, b) => a.creadoEn.localeCompare(b.creadoEn));
  const ultima = rechazadas[rechazadas.length - 1];
  if (ultima?.propuestaKcal != null && Math.abs(kcal - ultima.propuestaKcal) < DIFERENCIA_PARA_REOFRECER_KCAL) {
    return { ofrecer: false, razon: 'rechazada_sin_cambio', rechazadaKcal: ultima.propuestaKcal };
  }
  return { ofrecer: true };
}

/** "Te faltan tu estatura y tu fecha de nacimiento." (Req. 22.7) */
export function textoDeFaltantes(faltan: readonly DatoDeFormula[]): string {
  const nombres = faltan.map((d) => NOMBRE_DATO[d]);
  if (nombres.length === 0) return '';
  const lista = nombres.length === 1 ? nombres[0] : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
  return `Para calcularlo falta ${lista}.`;
}

// ---------------------------------------------------------------------------
// Mantenimiento estimado con los días registrados (Req. 22.4; design.md, 8.7)
// ---------------------------------------------------------------------------

/** La ventana que se mira: los 21 días anteriores a hoy. */
export const DIAS_VENTANA = 21;
/** Días con registro completo que hacen falta para estimar. */
export const DIAS_VALIDOS_MINIMOS = 14;
/** Entre la tendencia del primer día de la ventana y la del último hay 20 días. */
export const DIAS_ENTRE_TENDENCIAS = 20;
/** Equivalencia acordada para el cambio de peso. */
export const KCAL_POR_KG = 7700;
export const ESTIMADO_MIN_KCAL = 1000;
export const ESTIMADO_MAX_KCAL = 5000;

export interface DiaDeEstimacion {
  fecha: FechaLocal;
  /** kcal registradas ese día. */
  kcal: number;
  /** ¿Tiene al menos un registro de comida? */
  conRegistros: boolean;
  /** Marcado como "no registré todo lo que comí" (Req. 18.9). */
  incompleto: boolean;
}

export interface DatosDeEstimacion {
  /** La ventana completa, del día más antiguo al más reciente. */
  dias: readonly DiaDeEstimacion[];
  /** Tendencia del primer día de la ventana. */
  tendenciaInicioKg: number | null;
  /** Tendencia del último día de la ventana (ayer). */
  tendenciaFinKg: number | null;
  /** La tendencia de hoy, para los macros; null si no hay. */
  pesoActualKg: number | null;
}

export type MotivoEstimado = 'pocos_dias' | 'sin_tendencia' | 'fuera_de_rango';

export interface EstimadoDeMantenimiento {
  kcal: number;
  kcalPromedio: number;
  /** Cambio de la tendencia en la ventana; negativo si bajó. */
  deltaKg: number;
  /** Los días que entraron, para poder corregirlos (Req. 22.9). */
  diasValidos: DiaDeEstimacion[];
  pasos: string[];
  supuestos: string[];
  detalle: string;
  macros: MacrosPropuestos | null;
}

export type ResultadoEstimado =
  | { tipo: 'listo'; estimado: EstimadoDeMantenimiento }
  | { tipo: 'faltan'; motivo: MotivoEstimado; diasValidos: number; faltanDias: number; kcalFueraDeRango?: number };

/**
 * El mantenimiento estimado con lo registrado (Req. 22.4):
 *
 *     estimado = promedio de kcal − (cambio de la tendencia × 7,700 ÷ 20)
 *
 * Solo con 14 días o más de registro completo en la ventana y con tendencia
 * en sus dos extremos. Si falta algo, dice qué falta en lugar de estimar con
 * menos (Req. 22.7). Nunca cambia nada: es una propuesta (Req. 22.5).
 */
export function estimarMantenimiento(datos: DatosDeEstimacion): ResultadoEstimado {
  const validos = datos.dias.filter((d) => d.conRegistros && !d.incompleto);
  if (validos.length < DIAS_VALIDOS_MINIMOS) {
    return { tipo: 'faltan', motivo: 'pocos_dias', diasValidos: validos.length, faltanDias: DIAS_VALIDOS_MINIMOS - validos.length };
  }
  const { tendenciaInicioKg: inicio, tendenciaFinKg: fin } = datos;
  if (inicio === null || fin === null) {
    return { tipo: 'faltan', motivo: 'sin_tendencia', diasValidos: validos.length, faltanDias: 0 };
  }
  const kcalPromedio = validos.reduce((suma, d) => suma + d.kcal, 0) / validos.length;
  const deltaKg = fin - inicio;
  const porDia = (deltaKg * KCAL_POR_KG) / DIAS_ENTRE_TENDENCIAS;
  const kcal = redondearAMultiplo(kcalPromedio - porDia, 10);
  if (kcal < ESTIMADO_MIN_KCAL || kcal > ESTIMADO_MAX_KCAL) {
    return { tipo: 'faltan', motivo: 'fuera_de_rango', diasValidos: validos.length, faltanDias: 0, kcalFueraDeRango: kcal };
  }
  const primero = validos[0] as DiaDeEstimacion;
  const ultimo = validos[validos.length - 1] as DiaDeEstimacion;
  const bajo = deltaKg < 0;
  const cambio = unDecimal(Math.abs(deltaKg));
  const ajuste = sinDecimales(Math.abs(porDia));
  const pasos = [
    `${validos.length} de los ${datos.dias.length} días anteriores tienen registro completo, del ${formatearCorto(primero.fecha)} al ${formatearCorto(
      ultimo.fecha,
    )}.`,
    `Promedio de lo que registraste esos días: ${formatearKcal(kcalPromedio)} al día.`,
    deltaKg === 0
      ? `Tu tendencia de peso quedó igual (${unDecimalFijo(fin)} kg), así que no se ajusta nada.`
      : `Tu tendencia ${bajo ? 'bajó' : 'subió'} ${cambio} kg en ${DIAS_ENTRE_TENDENCIAS} días (de ${unDecimalFijo(
          inicio,
        )} a ${unDecimalFijo(fin)} kg).`,
    deltaKg === 0
      ? `Entonces tu mantenimiento es ese promedio: ${formatearKcal(kcal)} al día.`
      : `${cambio} kg × ${sinDecimales(KCAL_POR_KG)} kcal ÷ ${DIAS_ENTRE_TENDENCIAS} días = ${ajuste} kcal al día ${
          bajo ? 'de menos' : 'de más'
        } de tu mantenimiento.`,
    deltaKg === 0
      ? `Redondeado a 10: ${formatearKcal(kcal)}.`
      : `${formatearKcal(kcalPromedio)} ${bajo ? '+' : '−'} ${ajuste} = ${formatearKcal(kcal)} al día.`,
  ];
  const supuestos = [
    `Un kilo de peso equivale a ${sinDecimales(KCAL_POR_KG)} kcal.`,
    'Los días con registros cuentan como completos, salvo los que marcaste como "no registré todo lo que comí".',
    'La tendencia es el promedio de 7 días, no el pesaje de un solo día.',
    'Sale de lo que registraste: si algún día anotaste de menos, el estimado queda bajo.',
  ];
  const macros = datos.pesoActualKg === null ? null : macrosDeMantenimiento(kcal, datos.pesoActualKg);
  if (macros !== null) {
    supuestos.push(
      `Proteína de ${formatearNumero(PROTEINA_POR_KG, { decimales: 1 })} g por kg de peso y grasa del ${
        macros.porcentajes.grasa
      } % de las calorías; los carbohidratos son el resto. Es un punto de partida para revisar, no una indicación profesional.`,
    );
  }
  return {
    tipo: 'listo',
    estimado: {
      kcal,
      kcalPromedio,
      deltaKg,
      diasValidos: validos,
      pasos,
      supuestos,
      detalle: `Estimado con ${validos.length} días registrados (promedio ${formatearKcal(kcalPromedio)}) y un cambio de tendencia de ${
        deltaKg === 0 ? '0' : `${bajo ? '−' : '+'}${cambio}`
      } kg en ${DIAS_ENTRE_TENDENCIAS} días.`,
      macros,
    },
  };
}

/** "Te faltan 3 días con registro completo." (Req. 22.7) */
export function textoFaltaParaEstimar(r: Extract<ResultadoEstimado, { tipo: 'faltan' }>, dias: number): string {
  if (r.motivo === 'pocos_dias') {
    const tiene = `${r.diasValidos} de los ${dias} días anteriores ${r.diasValidos === 1 ? 'tiene' : 'tienen'} registro completo`;
    return `Hacen falta ${DIAS_VALIDOS_MINIMOS} días con registro completo de comidas y por ahora ${tiene}: ${
      r.faltanDias === 1 ? 'falta 1 día' : `faltan ${r.faltanDias} días`
    }.`;
  }
  if (r.motivo === 'sin_tendencia') {
    return `Falta tu tendencia de peso al principio y al final de esos ${dias} días: para eso hacen falta pesajes en los dos extremos.`;
  }
  return `El cálculo con tus datos dio ${formatearKcal(r.kcalFueraDeRango ?? 0)}, fuera del rango que la app considera razonable (${sinDecimales(
    ESTIMADO_MIN_KCAL,
  )} a ${sinDecimales(ESTIMADO_MAX_KCAL)} kcal). Revisa tus registros de esos días antes de usarlo.`;
}
